import { Fragment, useMemo, type ReactNode } from 'react';
import { isWorkspaceLink } from '../lib/workspace-links';
import { CitationLink } from '@/citations/citation-link';
import { citationTarget } from '@/citations/reference';
import { splitMarked } from './markdown-marks';
import { parseMarkdownTree, type MarkdownNode as Node } from './markdown-parse';
import { safeSourceUrl } from './safe-url';
import './markdown.css';

export { safeSourceUrl } from './safe-url';

/**
 * How tracked changes carried in the text (markdown-marks.ts) are shown:
 * `mark` wraps removed or inserted words of one suggestion, `marker` is its
 * numbered marker. Without it the text renders as plain Markdown.
 */
export interface MarkRenderer {
  mark: (kind: 'del' | 'ins', index: number, content: string, key: string) => ReactNode;
  marker: (index: number, key: string) => ReactNode;
  /** A block with no text (a rule) inside a removed or inserted run. */
  block?: (kind: 'del' | 'ins', index: number, content: ReactNode, key: string) => ReactNode;
}

/**
 * The renderer for one Markdown tree. Tracked-change state runs through the
 * whole tree in document order, so a removed or inserted run may cross
 * element boundaries (it is wrapped piece by piece). A marker inside a link
 * is held back and shown right after the link.
 */
function createRenderer(marks?: MarkRenderer) {
  let open: { kind: 'del' | 'ins'; index: number } | null = null;
  let links = 0;
  let held: number[] = [];
  let serial = 0;
  const pieces = (value: string): ReactNode[] => {
    if (!marks) return [value];
    const out: ReactNode[] = [];
    for (const piece of splitMarked(value)) {
      if (typeof piece === 'string')
        out.push(open ? marks.mark(open.kind, open.index, piece, `m${serial++}`) : piece);
      else if (piece.kind === 'close') open = null;
      else if (piece.kind === 'mark') {
        if (links) held.push(piece.index);
        else out.push(marks.marker(piece.index, `k${serial++}`));
      } else open = { kind: piece.kind, index: piece.index };
    }
    return out;
  };
  const release = () => {
    if (!marks || links || !held.length) return [];
    const markers = held.map((index) => marks.marker(index, `k${serial++}`));
    held = [];
    return markers;
  };

  // A GFM table as the answer shows it (DESIGN-SPEC §3.5): its first row is the header,
  // columns keep the alignment the model wrote, and the table scrolls inside its own
  // wrapper instead of squeezing its columns until words break.
  function renderTable(node: Node, key: number) {
    const [head, ...rows] = node.children ?? [];
    const cells = (row: Node, header: boolean) =>
      row.children?.map((cell, index) => {
        const Cell = header ? 'th' : 'td';
        return (
          <Cell key={index} data-align={node.align?.[index] ?? undefined}>
            {cell.children?.map(render)}
          </Cell>
        );
      });
    return (
      <div className="ws-table-scroll" key={key}>
        <table>
          {head && (
            <thead>
              <tr>{cells(head, true)}</tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>{cells(row, false)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // A server-shaped citation link becomes the same source chip the chat answer shows;
  // other internal links stay in the app and external ones open safely in a new tab.
  function renderLink(node: Node, key: number) {
    links += 1;
    const children = node.children?.map(render);
    links -= 1;
    const internal = isWorkspaceLink(node.url);
    const url = internal ? node.url : safeSourceUrl(node.url);
    const link =
      internal && citationTarget(node.url) ? (
        <CitationLink key={key} href={node.url}>
          {children}
        </CitationLink>
      ) : url ? (
        <a key={key} href={url} target={internal ? '_self' : '_blank'} rel="noopener noreferrer">
          {children}
        </a>
      ) : (
        <span key={key}>{children}</span>
      );
    const after = release();
    return after.length ? (
      <Fragment key={key}>
        {link}
        {after}
      </Fragment>
    ) : (
      link
    );
  }

  function render(node: Node, key: number): ReactNode {
    if (node.type === 'link') return renderLink(node, key);
    if (node.type === 'text') return <Fragment key={key}>{pieces(node.value ?? '')}</Fragment>;
    if (node.type === 'thematicBreak') {
      const rule = <hr key={key} />;
      return open && marks?.block ? marks.block(open.kind, open.index, rule, `b${key}`) : rule;
    }
    const children = node.children?.map(render);
    switch (node.type) {
      case 'paragraph':
        return <p key={key}>{children}</p>;
      case 'heading':
        return node.depth === 1 ? <h2 key={key}>{children}</h2> : <h3 key={key}>{children}</h3>;
      case 'strong':
        return <strong key={key}>{children}</strong>;
      case 'emphasis':
        return <em key={key}>{children}</em>;
      case 'delete':
        return <del key={key}>{children}</del>;
      case 'list':
        return node.ordered ? <ol key={key}>{children}</ol> : <ul key={key}>{children}</ul>;
      case 'listItem':
        return <li key={key}>{children}</li>;
      case 'blockquote':
        return <blockquote key={key}>{children}</blockquote>;
      case 'inlineCode':
        return <code key={key}>{pieces(node.value ?? '')}</code>;
      case 'code':
        return (
          <pre key={key}>
            <code>{pieces(node.value ?? '')}</code>
          </pre>
        );
      case 'image':
        return null;
      case 'html':
        return <span key={key}>{pieces(node.value ?? '')}</span>;
      case 'break':
        return <br key={key} />;
      case 'table':
        return renderTable(node, key);
      default:
        return <span key={key}>{children}</span>;
    }
  }
  return render;
}
/** Company text is data: no raw HTML, remote images, scripts or generated UI. */
export function WorkspaceMarkdown({ text, marks }: { text: string; marks?: MarkRenderer }) {
  const tree = useMemo(() => parseMarkdownTree(text), [text]);
  const render = createRenderer(marks);
  return <div className="ws-markdown">{tree.children?.map(render)}</div>;
}

export { completeMarkdown, inlinePlainText } from './plain-text';
